import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { Hotel, Ota } from 'frontend-utils/models';
import ParityTooltipsTooltipPosTableLoss from 'lumen/components/parity/tooltips/tooltip-pos-table-loss';
import type { Period } from 'lumen/models';
import { filterMajorOTAs, filterParities } from 'lumen/utils/parities-filter';
import {
  type BaseRate,
  formatBaseRate,
  formatParity,
  type FormattedParity,
  sortParities,
} from 'lumen/utils/parity-utils';

const MAX_NB_OF_OTAS_IN_CELL = 3;

export interface TdPosOtasWithIssuesColumn {
  availableOTAs: Ota[];
  searchMajorOtas: boolean;
}

interface ParityTablesCellsTdPosOtasWithIssuesSignature {
  Args: {
    cellData: Period;
    column: TdPosOtasWithIssuesColumn;
  };
}

export default class ParityTablesCellsTdPosOtasWithIssues extends Component<ParityTablesCellsTdPosOtasWithIssuesSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare ota: Services['ota'];
  @service declare rates: Services['rates'];

  get baseRates(): BaseRate[] {
    const { cellData, column } = this.args;

    const otaIds = column.availableOTAs?.map((ota) => ota.id);

    const baseRates = filterMajorOTAs(
      otaIds,
      // @ts-expect-error: Argument of type 'ParityDict | null' is not assignable to parameter of type 'ParityDict'.
      cellData.paritiesPOSDict,
      (parity) => parity.is_baserate,
    );

    return baseRates.map((baseRate) =>
      formatBaseRate(this.ota, this.rates, this.hotel, baseRate),
    );
  }

  get channelNames(): string {
    const channelNames = this.formattedChannels.map((channel) => channel.title);
    if (channelNames.length > MAX_NB_OF_OTAS_IN_CELL) {
      return `${channelNames.slice(0, MAX_NB_OF_OTAS_IN_CELL).join(',')}...`;
    }
    if (channelNames.length) {
      return channelNames.join(',');
    }
    return this.intl.t('parity.no_issues');
  }

  get formattedChannels(): FormattedParity[] {
    if (!this.period.paritiesPOSDict) {
      return [];
    }

    const parities = filterParities(
      this.period.paritiesPOSDict,
      this.args.column.searchMajorOtas,
      this.args.column.availableOTAs,
      (parity) => parity.value !== 0 && parity.position_to_baserate < 0,
    );

    return parities
      .sort(sortParities)
      .slice(0, 15)
      .map((parity) => formatParity(this.ota, this.rates, this.hotel, parity));
  }

  get hotel(): Hotel | null {
    return this.hotelAndMarketService.hotel;
  }

  get isToday(): boolean {
    return this.period.day.isToday;
  }

  get period(): Period {
    return this.args.cellData;
  }

  <template>
    <div
      class={{if this.isToday "td-today"}}
      data-test-id="pos-otas-with-issues-cell"
    >
      <p>{{this.channelNames}}</p>

      <ParityTooltipsTooltipPosTableLoss
        @baseRates={{this.baseRates}}
        @formattedChannels={{this.formattedChannels}}
      />
    </div>
  </template>
}
